<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Select, SelectOptGroup, SelectOption, Button, Spin } from 'ant-design-vue'
import { AlertCircle, RefreshCw } from 'lucide-vue-next'
import { InjectionService } from '@/shared/services/InjectionService'
import {
  TemplateApiService,
  type ITemplateApiService,
} from './services/TemplateApiService'
import type { AvailableTemplate, TemplateSelection } from './models'

// --- Props & Emits ---
const props = defineProps<{
  modelValue: TemplateSelection | null
  label?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: TemplateSelection]
}>()

// --- Services ---
const templateApiService: ITemplateApiService =
  InjectionService.injectStrict(TemplateApiService.injectableKey)

// --- State ---
const templates = ref<AvailableTemplate[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// --- Computed ---
const systemTemplates = computed(() =>
  templates.value.filter((t) => t.ownershipLevel === 'system')
)

const tenantTemplates = computed(() =>
  templates.value.filter((t) => t.ownershipLevel === 'tenant')
)

const isEmpty = computed(() => templates.value.length === 0 && !loading.value && !error.value)

const hasError = computed(() => !!error.value)

const selectedValue = computed(() => {
  if (!props.modelValue) return undefined
  return `${props.modelValue.template_id}::${props.modelValue.version}`
})

// --- Methods ---
async function fetchTemplates() {
  loading.value = true
  error.value = null

  try {
    const response = await templateApiService.getAvailableTemplates()
    templates.value = response.data

    // Auto-select first system template if no current selection
    if (!props.modelValue && systemTemplates.value.length > 0) {
      const first = systemTemplates.value[0]
      emit('update:modelValue', {
        template_id: first.templateId,
        version: first.version,
        name: first.name,
        ownership_level: first.ownershipLevel,
      })
    } else if (!props.modelValue && tenantTemplates.value.length > 0) {
      // Fallback to first tenant template if no system templates
      const first = tenantTemplates.value[0]
      emit('update:modelValue', {
        template_id: first.templateId,
        version: first.version,
        name: first.name,
        ownership_level: first.ownershipLevel,
      })
    }
  } catch (err: any) {
    error.value =
      err?.response?.data?.message ||
      err?.message ||
      'Failed to load templates. Please try again.'
  } finally {
    loading.value = false
  }
}

function handleSelect(value: string) {
  const [templateId, versionStr] = value.split('::')
  const version = parseInt(versionStr, 10)
  const template = templates.value.find(
    (t) => t.templateId === templateId && t.version === version
  )
  if (template) {
    emit('update:modelValue', {
      template_id: template.templateId,
      version: template.version,
      name: template.name,
      ownership_level: template.ownershipLevel,
    })
  }
}

function handleRetry() {
  fetchTemplates()
}

function getOptionValue(template: AvailableTemplate): string {
  return `${template.templateId}::${template.version}`
}

function getOptionLabel(template: AvailableTemplate): string {
  return `${template.name} (v${template.version})`
}

// --- Lifecycle ---
onMounted(() => {
  fetchTemplates()
})
</script>

<template>
  <div data-testid="template-selector">
    <label v-if="label" class="mb-1.5 block text-sm font-medium">{{ label }}</label>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center gap-2" data-testid="template-selector-loading">
      <Spin size="small" />
      <span class="text-sm text-muted-foreground">Loading templates...</span>
    </div>

    <!-- Error State -->
    <div
      v-else-if="hasError"
      class="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950"
      data-testid="template-selector-error"
    >
      <AlertCircle class="size-4 flex-shrink-0 text-red-500" />
      <span class="flex-1 text-sm text-red-600 dark:text-red-300">{{ error }}</span>
      <Button size="small" @click="handleRetry" data-testid="template-selector-retry-btn">
        <RefreshCw class="mr-1 size-3" />
        Retry
      </Button>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="isEmpty"
      class="rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-950"
      data-testid="template-selector-empty"
    >
      <span class="text-sm text-yellow-700 dark:text-yellow-300">No templates available</span>
    </div>

    <!-- Select Dropdown -->
    <Select
      v-else
      :value="selectedValue"
      style="width: 100%"
      placeholder="Select a template"
      @change="handleSelect"
      data-testid="template-selector-dropdown"
    >
      <SelectOptGroup v-if="systemTemplates.length > 0" label="System Templates">
        <SelectOption
          v-for="tmpl in systemTemplates"
          :key="getOptionValue(tmpl)"
          :value="getOptionValue(tmpl)"
        >
          {{ getOptionLabel(tmpl) }}
        </SelectOption>
      </SelectOptGroup>
      <SelectOptGroup v-if="tenantTemplates.length > 0" label="My Organization">
        <SelectOption
          v-for="tmpl in tenantTemplates"
          :key="getOptionValue(tmpl)"
          :value="getOptionValue(tmpl)"
        >
          {{ getOptionLabel(tmpl) }}
        </SelectOption>
      </SelectOptGroup>
    </Select>
  </div>
</template>
